import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, BarChart3, Play, Square, FileText, X, Users } from "lucide-react";
import { api, errMsg, fdate } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { useAuth } from "@/lib/auth";
import { PageHeader, Loading, Empty, DemoBadge, LiveIndicator } from "@/components/common";
import { ResultBars } from "@/pages/citizen/Surveys";

const EMPTY_Q = { text: "", type: "single", options: ["", ""], required: true };
const EMPTY = { title: "", description: "", status: "borrador", ends_at: "", questions: [{ ...EMPTY_Q }], segment: { municipios: [], colonias: [], age_ranges: [], citizen_types: [] } };
const STATE = {
  borrador: { cls: "bg-slate-100 text-slate-700 border-slate-200", label: "Borrador" },
  activa: { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "Activa" },
  cerrada: { cls: "bg-rose-50 text-rose-800 border-rose-200", label: "Cerrada" },
};

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);

function TagMulti({ label, options, value, onChange, testid }) {
  const toggle = (opt) => { const next = value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]; onChange(next); };
  return (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={testid}>
        {options.map((o) => {
          const on = value.includes(o);
          return (
            <button type="button" key={o} onClick={() => toggle(o)}
              className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${on ? "bg-[#007C91] text-white border-[#007C91]" : "bg-white hover:bg-secondary"}`}>
              {o}
            </button>
          );
        })}
        {!options.length && <span className="text-xs text-muted-foreground">—</span>}
      </div>
    </div>
  );
}

function CSV({ data }) {
  const run = () => {
    const rows = [["Pregunta", "Opción/Nota", "Conteo", "%"]];
    data.questions.forEach((q) => {
      const total = Math.max(1, q.answered);
      if (q.type === "text") (q.samples || []).forEach((s) => rows.push([q.text, s, "", ""]));
      else Object.entries(q.counts || {}).forEach(([o, n]) => rows.push([q.text, o, String(n), `${Math.round((n / total) * 100)}%`]));
    });
    const blob = new Blob(["\ufeff" + rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "resultados.csv"; a.click();
  };
  return <Button variant="outline" size="sm" onClick={run} data-testid="survey-export-csv"><FileText className="h-4 w-4 mr-1.5" />Exportar CSV</Button>;
}

export default function SurveysAdmin() {
  const { user } = useAuth();
  const canEdit = user.role === "funcionario";
  const list = useFetch("/intranet/surveys", { poll: 15000 });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [results, setResults] = useState(null);
  const live = useFetch(results ? `/intranet/surveys/${results.survey.survey_id}/results` : null, { poll: 5000, deps: [results?.survey?.survey_id] });

  const items = list.data?.items || [];
  const types = list.data?.question_types || { single: "Opción única", multiple: "Opción múltiple", scale: "Escala 1–5", text: "Respuesta abierta" };
  const ages = list.data?.age_ranges || [];
  const kinds = list.data?.citizen_types || [];
  const munis = list.data?.municipios || [];

  const edit = (s) => setForm(s ? JSON.parse(JSON.stringify({ ...EMPTY, ...s, ends_at: toLocalInput(s.ends_at) })) : JSON.parse(JSON.stringify(EMPTY)));
  const setQ = (i, patch) => setForm((f) => ({ ...f, questions: f.questions.map((q, j) => (j === i ? { ...q, ...patch } : q)) }));
  const addQ = () => setForm((f) => ({ ...f, questions: [...f.questions, { ...EMPTY_Q, options: ["", ""] }] }));
  const rmQ = (i) => setForm((f) => ({ ...f, questions: f.questions.filter((_, j) => j !== i) }));
  const setOpt = (qi, oi, v) => setQ(qi, { options: form.questions[qi].options.map((o, k) => (k === oi ? v : o)) });
  const addOpt = (qi) => setQ(qi, { options: [...form.questions[qi].options, ""] });
  const rmOpt = (qi, oi) => setQ(qi, { options: form.questions[qi].options.filter((_, k) => k !== oi) });

  const save = async () => {
    setSaving(true);
    try {
      if (!form.title?.trim() || form.title.trim().length < 4) throw { response: { data: { detail: "El título debe tener al menos 4 caracteres" } } };
      const body = {
        title: form.title.trim(), description: (form.description || "").trim(),
        status: form.status, ends_at: fromLocalInput(form.ends_at),
        segment: form.segment,
        questions: form.questions.map((q, i) => ({
          qid: q.qid, text: q.text.trim(), type: q.type, required: !!q.required,
          options: (q.type === "single" || q.type === "multiple") ? q.options.map((o) => o.trim()).filter(Boolean) : [],
        })),
      };
      if (form.survey_id) await api.put(`/intranet/surveys/${form.survey_id}`, body);
      else await api.post("/intranet/surveys", body);
      toast.success(form.survey_id ? "Encuesta actualizada" : "Encuesta creada");
      setForm(null); list.reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const changeStatus = async (s, status) => {
    try { await api.patch(`/intranet/surveys/${s.survey_id}/status`, { status }); toast.success(status === "activa" ? "Encuesta publicada" : status === "cerrada" ? "Encuesta cerrada" : "Movida a borrador"); list.reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const openResults = async (s) => {
    try { const { data } = await api.get(`/intranet/surveys/${s.survey_id}/results`); setResults(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const setSeg = (key, val) => setForm((f) => ({ ...f, segment: { ...f.segment, [key]: val } }));

  return (
    <div>
      <PageHeader
        eyebrow="Gobierno · Participación"
        title="Gestión de encuestas"
        subtitle="Diseña, segmenta y publica encuestas con resultados en vivo."
        actions={canEdit ? <Button onClick={() => edit(null)} data-testid="survey-new-button"><Plus className="h-4 w-4 mr-1.5" />Nueva encuesta</Button> : null}
      />
      <div className="flex items-center gap-2 mb-4"><LiveIndicator lastSync={list.lastSync} /><DemoBadge /></div>

      {list.loading ? <Loading rows={4} /> : !items.length ? (
        <Empty title="Aún no hay encuestas" hint="Crea la primera para empezar a recopilar opinión ciudadana." icon={BarChart3} action={canEdit && <Button onClick={() => edit(null)}>Nueva encuesta</Button>} />
      ) : (
        <div className="grid gap-3" data-testid="surveys-admin-list">
          {items.map((s) => {
            const st = STATE[s.status] || STATE.borrador;
            const seg = s.segment || {};
            const segChips = [
              seg.municipios?.length && `${seg.municipios.length} municipio(s)`,
              seg.colonias?.length && `${seg.colonias.length} colonia(s)`,
              seg.age_ranges?.length && `${seg.age_ranges.length} rango(s) edad`,
              seg.citizen_types?.length && `${seg.citizen_types.length} tipo(s) ciudadano`,
            ].filter(Boolean);
            return (
              <Card key={s.survey_id} data-testid={`survey-admin-${s.survey_id}`}>
                <CardContent className="p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={st.cls}>{st.label}</Badge>
                      {s.is_demo && <DemoBadge />}
                      <span className="text-xs text-muted-foreground">· {s.questions?.length || 0} pregunta(s) · <b className="text-foreground font-mono">{s.responses || 0}</b> respuestas</span>
                    </div>
                    <div className="font-medium mt-1 truncate">{s.title}</div>
                    {s.description && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{s.description}</div>}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {segChips.length ? segChips.map((c, i) => <Badge key={i} variant="secondary" className="text-[10px]">{c}</Badge>) : <Badge variant="secondary" className="text-[10px]"><Users className="h-3 w-3 mr-1" />Abierta a todos</Badge>}
                      {s.ends_at && <Badge variant="outline" className="text-[10px]">cierra {fdate(s.ends_at)}</Badge>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => openResults(s)} data-testid={`survey-results-${s.survey_id}`}><BarChart3 className="h-4 w-4 mr-1.5" />Resultados</Button>
                    {canEdit && s.status === "borrador" && <Button size="sm" onClick={() => changeStatus(s, "activa")} data-testid={`survey-publish-${s.survey_id}`}><Play className="h-4 w-4 mr-1.5" />Publicar</Button>}
                    {canEdit && s.status === "activa" && <Button size="sm" variant="outline" onClick={() => changeStatus(s, "cerrada")}><Square className="h-4 w-4 mr-1.5" />Cerrar</Button>}
                    {canEdit && <Button size="sm" variant="ghost" onClick={() => edit(s)} data-testid={`survey-edit-${s.survey_id}`}><Pencil className="h-4 w-4" /></Button>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Editor */}
      <Sheet open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto bg-white">
          <SheetHeader><SheetTitle>{form?.survey_id ? "Editar encuesta" : "Nueva encuesta"}</SheetTitle></SheetHeader>
          {form && (
            <div className="space-y-4 mt-4" data-testid="survey-form">
              <div className="grid gap-1.5"><Label>Título</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="survey-title" /></div>
              <div className="grid gap-1.5"><Label>Descripción</Label><Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="survey-description" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5"><Label>Estatus</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="survey-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(STATE).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="grid gap-1.5"><Label>Cierre (opcional)</Label><Input type="datetime-local" value={form.ends_at || ""} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} data-testid="survey-ends-at" /></div>
              </div>

              <div className="rounded-xl border p-3 space-y-3">
                <div className="text-sm font-medium">Segmentación</div>
                <TagMulti label="Municipios" options={munis} value={form.segment.municipios} onChange={(v) => setSeg("municipios", v)} testid="seg-municipios" />
                <TagMulti label="Rangos de edad" options={ages} value={form.segment.age_ranges} onChange={(v) => setSeg("age_ranges", v)} testid="seg-ages" />
                <TagMulti label="Tipo de ciudadano" options={kinds} value={form.segment.citizen_types} onChange={(v) => setSeg("citizen_types", v)} testid="seg-types" />
                <div>
                  <Label className="text-xs text-muted-foreground">Colonias (separadas por coma)</Label>
                  <Input placeholder="Centro, Supermanzana 20, …" value={form.segment.colonias.join(", ")} onChange={(e) => setSeg("colonias", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid="seg-colonias" />
                </div>
                <p className="text-[11px] text-muted-foreground">Deja todo en blanco para abrir la encuesta a cualquier ciudadano.</p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between"><div className="text-sm font-medium">Preguntas</div><Button size="sm" variant="outline" onClick={addQ} data-testid="survey-add-question"><Plus className="h-4 w-4 mr-1.5" />Agregar</Button></div>
                {form.questions.map((q, qi) => (
                  <Card key={qi} className="border-dashed">
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="flex-1 grid gap-1.5"><Label className="text-xs">Pregunta {qi + 1}</Label><Input value={q.text} onChange={(e) => setQ(qi, { text: e.target.value })} data-testid={`q-text-${qi}`} /></div>
                        <Button size="icon" variant="ghost" onClick={() => rmQ(qi)} disabled={form.questions.length === 1} data-testid={`q-remove-${qi}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="grid gap-1.5"><Label className="text-xs">Tipo</Label>
                          <Select value={q.type} onValueChange={(v) => setQ(qi, { type: v, options: (v === "single" || v === "multiple") && q.options.length < 2 ? ["", ""] : q.options })}>
                            <SelectTrigger data-testid={`q-type-${qi}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{Object.entries(types).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                          </Select></div>
                        <label className="flex items-end gap-2 text-xs"><Checkbox checked={q.required} onCheckedChange={(v) => setQ(qi, { required: !!v })} data-testid={`q-required-${qi}`} />Obligatoria</label>
                      </div>
                      {(q.type === "single" || q.type === "multiple") && (
                        <div className="space-y-1.5">
                          <Label className="text-xs">Opciones</Label>
                          {q.options.map((o, oi) => (
                            <div key={oi} className="flex items-center gap-2">
                              <Input value={o} onChange={(e) => setOpt(qi, oi, e.target.value)} placeholder={`Opción ${oi + 1}`} data-testid={`q-opt-${qi}-${oi}`} />
                              <Button size="icon" variant="ghost" onClick={() => rmOpt(qi, oi)} disabled={q.options.length <= 2}><X className="h-4 w-4" /></Button>
                            </div>
                          ))}
                          <Button size="sm" variant="outline" onClick={() => addOpt(qi)}><Plus className="h-3.5 w-3.5 mr-1" />Agregar opción</Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t">
                <Button variant="ghost" onClick={() => setForm(null)}>Cancelar</Button>
                <Button onClick={save} disabled={saving} data-testid="survey-save-button">{saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}Guardar</Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Results */}
      <Dialog open={!!results} onOpenChange={(v) => !v && setResults(null)}>
        <DialogContent className="bg-white sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5 text-[#007C91]" />Resultados en vivo</DialogTitle>
            <DialogDescription>{results?.survey?.title}</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between">
            <LiveIndicator lastSync={live.lastSync} />
            {(live.data || results) && <CSV data={live.data || results} />}
          </div>
          <div className="max-h-[60vh] overflow-y-auto pr-2">
            <ResultBars results={live.data || results} testid="survey-admin-results" />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
